// Pure stop-detection presentation logic for Operational Center.
//
// Single source of truth for how the DISPLAY layer turns the
// backend-authoritative stop state (the *_arrival_detected_at /
// *_dwell_confirmed_at / *_departure_detected_at columns written by the 5-mile
// stop-detection engine) and the possible_missed_* candidate alerts into what
// a dispatcher sees. DISPLAY ONLY -- nothing here mutates a load; Confirm/
// Dismiss go through the canonical /fleet/resolve-missed-stop endpoint.
//
// Kept pure (no React, no fetch, no clock -- `nowMs` is passed in) so the whole
// decision surface is unit-testable, mirroring the backend's stop_detection.py.

export const MISSED_TYPES = new Set(["possible_missed_pickup", "possible_missed_delivery"]);

// A stop still counts as "at facility" this long past its appointment before we
// escalate the visual emphasis -- an early-arrival sleeper stays calm; a truck
// still sitting materially past its appointment gets highlighted. Not
// "detention" (no billing meaning yet); just dispatcher awareness.
export const APPT_PASSED_EMPHASIS_GRACE_MIN = 60;

export function parseUtcNaive(isoLike) {
  if (!isoLike) return null;
  if (isoLike instanceof Date) return Number.isNaN(isoLike.getTime()) ? null : isoLike;
  const s = String(isoLike);
  const iso = s.endsWith("Z") || /[+-]\d\d:\d\d$/.test(s) ? s : `${s}Z`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDwell(minutes) {
  if (minutes == null || Number.isNaN(minutes)) return "";
  const m = Math.max(0, Math.round(minutes));
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return h > 0 ? `${h}h ${mm}m` : `${mm}m`;
}

// Derive the stop state of a truck's PHYSICAL/current load for its current leg.
// status is the board's truckStatus() value ("heading_to_pickup" | "loaded" |
// "free"). Returns null when there's nothing stop-relevant (free truck, or a
// leg the engine hasn't touched). Because it keys off the physical load only,
// a queued 2nd/3rd load (d5b492e2) can never produce an at-pickup state while
// the truck carries another physical load.
export function deriveStopState(load, status, nowMs) {
  if (!load) return null;
  let leg, arrival, dwellConfirmed, departure, closest, appt;
  if (status === "heading_to_pickup" && !load.actual_pickup_at) {
    leg = "pickup";
    arrival = load.pickup_arrival_detected_at;
    dwellConfirmed = load.pickup_dwell_confirmed_at;
    departure = load.pickup_departure_detected_at;
    closest = load.pickup_closest_distance_miles;
    appt = load.pickup_datetime;
  } else if (status === "loaded" && !load.actual_delivery_at) {
    leg = "delivery";
    arrival = load.delivery_arrival_detected_at;
    dwellConfirmed = load.delivery_dwell_confirmed_at;
    departure = load.delivery_departure_detected_at;
    closest = load.delivery_closest_distance_miles;
    appt = load.delivery_datetime;
  } else {
    return null;
  }
  const arrMs = arrival ? parseUtcNaive(arrival)?.getTime() : null;
  if (arrMs == null) return { leg, atFacility: false };

  // At facility = arrived and NOT yet departed. Once departure is stamped the
  // truck has left -> it's a possible_missed_* candidate (handled via alerts),
  // never shown here as "still at facility".
  const atFacility = !departure;
  const endMs = departure ? parseUtcNaive(departure)?.getTime() : nowMs;
  const dwellMinutes = endMs != null ? Math.max(0, Math.round((endMs - arrMs) / 60000)) : null;
  const apptMs = appt ? parseUtcNaive(appt)?.getTime() : null;
  const apptPassed = apptMs != null && nowMs > apptMs;
  const apptDeltaMin = apptMs != null ? Math.round((nowMs - apptMs) / 60000) : null;
  const emphasize = atFacility && apptMs != null && nowMs > apptMs + APPT_PASSED_EMPHASIS_GRACE_MIN * 60000;

  return {
    leg,
    atFacility,
    phase: dwellConfirmed ? "dwelling" : "arrived",
    arrivalAt: arrival,
    dwellConfirmedAt: dwellConfirmed,
    departureAt: departure,
    closestMiles: closest != null ? Number(closest) : null,
    apptAt: appt,
    apptPassed,
    apptDeltaMin,
    dwellMinutes,
    emphasize,
  };
}

// Plain-English one-liner for a candidate (no engine reason codes).
export function humanEvidence(candidate) {
  const m = candidate || {};
  const leg = m.leg || (m.type === "possible_missed_pickup" ? "pickup" : "delivery");
  const place = leg === "pickup" ? "pickup" : "delivery";
  const dwell = m.dwell_minutes != null ? formatDwell(m.dwell_minutes) : "a while";
  const away = m.current_distance_miles != null ? `${Math.round(m.current_distance_miles)} miles away` : "away from it";
  const lifecycle = leg === "pickup" ? "the pickup is still unconfirmed" : "the load is still marked In Transit";
  return `Truck remained near the ${place} for ${dwell} and has since driven ${away}, but ${lifecycle}.`;
}

// Short calm label for an at-facility state, e.g. "At delivery 2h 18m".
export function atFacilityLabel(stop) {
  const noun = stop.leg === "pickup" ? "At pickup" : "At delivery";
  return `${noun} ${formatDwell(stop.dwellMinutes)}`.trim();
}

const CONF_RANK = { VERY_HIGH: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };

// Partition the dispatcher's OWN operational responsibility into the two panel
// groups. Scoped exactly like the legacy reminder: strict truck.dispatcher_id
// === currentUserId (reuse, don't invent a second ownership model). Candidates
// come from the already-server-scoped alerts feed but are re-intersected with
// owned trucks for safety.
export function partitionStopIntel(enriched, alerts, currentUserId, nowMs) {
  const mine = (enriched || []).filter((e) => e.truck && e.truck.dispatcher_id?.id === currentUserId);
  const myTruckIds = new Set(mine.map((e) => e.truck.id));

  const atFacility = [];
  for (const e of mine) {
    const stop = deriveStopState(e.load, e.status, nowMs);
    if (stop && stop.atFacility) {
      atFacility.push({ truck: e.truck, load: e.load, status: e.status, stop });
    }
  }
  // calm first-glance ordering: emphasized (appt passed) on top, then longest dwell
  atFacility.sort((a, b) =>
    (b.stop.emphasize - a.stop.emphasize) || ((b.stop.dwellMinutes || 0) - (a.stop.dwellMinutes || 0)));

  const actionRequired = (alerts || [])
    .filter((a) => (MISSED_TYPES.has(a.type) || a.type === "pickup_overdue") && myTruckIds.has(a.truck_id))
    .sort((a, b) => {
      const am = MISSED_TYPES.has(a.type), bm = MISSED_TYPES.has(b.type);
      if (am !== bm) return am ? -1 : 1;                       // missed candidates before overdue
      if (am && bm) return (CONF_RANK[a.confidence] ?? 9) - (CONF_RANK[b.confidence] ?? 9);
      return (b.overdue_minutes || 0) - (a.overdue_minutes || 0);
    });

  return { atFacility, actionRequired };
}

// Auto-expand decision for the Stop Intelligence panel. Given the set of
// action-alert ids seen last time and the CURRENT action-required alerts,
// returns the next id-set and whether the panel should force-open because a
// NEW actionable alert appeared. Identity is the stable alert_event_id -- never
// array position or count -- so an A->B replacement (count still 1) still
// counts as new and reopens. AT FACILITY is deliberately NOT an input here: it
// can never force the panel open. The panel is only ever force-OPENED (never
// force-collapsed) by the caller, so a dispatcher's manual collapse persists
// until a genuinely new action arrives.
export function computeAutoExpand(prevIdSet, actionAlerts) {
  const ids = new Set((actionAlerts || []).map((a) => a.alert_event_id).filter(Boolean));
  let open = false;
  for (const id of ids) {
    if (!prevIdSet || !prevIdSet.has(id)) { open = true; break; }
  }
  return { open, ids };
}

// Collapsed-header label: surfaces the ACTION count when actions exist (so
// "2 action required" is obvious even collapsed), otherwise the AT FACILITY
// count, otherwise nothing.
export function stopIntelHeaderLabel(actionCount, atFacilityCount) {
  if (actionCount > 0) return `${actionCount} action required`;
  if (atFacilityCount > 0) return `${atFacilityCount} at facility`;
  return null;
}

// Which facility timezone the leg's appointment/clock belongs to. Pickup uses
// the origin facility, delivery the destination facility. Returns null when a
// load predates timezone capture -> the board's formatter falls back to the
// browser zone (existing canonical behavior), never a wrong facility.
export function facilityTzForLeg(load, leg) {
  if (!load) return null;
  return (leg === "pickup" ? load.origin_timezone : load.destination_timezone) || null;
}

// The open candidate (if any) for a specific load -> drives the truck-card chip
// and the Current Load candidate block.
export function candidateForLoad(alerts, loadId) {
  if (!loadId) return null;
  return (alerts || []).find((a) => MISSED_TYPES.has(a.type) && a.load_id === loadId) || null;
}
